import type { TaskItem } from '@/api/types';

export interface WorkflowStage {
  tag: string;
  key: string;
  label: string;
  shortLabel: string;
  rank: number;
  color: string;
  bg: string;
}

const STAGES: WorkflowStage[] = [
  { tag: 'workflow:idea', key: 'idea', label: 'Idea', shortLabel: 'Idea', rank: 1, color: '#94a3b8', bg: 'rgba(148, 163, 184, 0.12)' },
  { tag: 'workflow:triage', key: 'triage', label: 'Triage', shortLabel: 'Triage', rank: 2, color: '#38bdf8', bg: 'rgba(56, 189, 248, 0.12)' },
  { tag: 'workflow:prompt-draft', key: 'prompt-draft', label: 'Prompt draft', shortLabel: 'Prompt', rank: 3, color: '#22d3ee', bg: 'rgba(34, 211, 238, 0.12)' },
  { tag: 'workflow:ready-for-agent', key: 'ready-for-agent', label: 'Ready for agent', shortLabel: 'Ready', rank: 4, color: '#39FF14', bg: 'rgba(57, 255, 20, 0.10)' },
  { tag: 'workflow:claimed-by-agent', key: 'claimed-by-agent', label: 'Claimed by agent', shortLabel: 'Claimed', rank: 5, color: '#84cc16', bg: 'rgba(132, 204, 22, 0.12)' },
  { tag: 'workflow:implementation', key: 'implementation', label: 'Implementation', shortLabel: 'Impl', rank: 6, color: '#a78bfa', bg: 'rgba(167, 139, 250, 0.12)' },
  { tag: 'workflow:agent-review', key: 'agent-review', label: 'Agent review', shortLabel: 'Agent review', rank: 7, color: '#facc15', bg: 'rgba(250, 204, 21, 0.12)' },
  { tag: 'workflow:human-review', key: 'human-review', label: 'Human review', shortLabel: 'Human review', rank: 8, color: '#fb7185', bg: 'rgba(251, 113, 133, 0.12)' },
  { tag: 'workflow:pr-ready', key: 'pr-ready', label: 'PR ready', shortLabel: 'PR ready', rank: 9, color: '#60a5fa', bg: 'rgba(96, 165, 250, 0.12)' },
  { tag: 'workflow:merged', key: 'merged', label: 'Merged', shortLabel: 'Merged', rank: 10, color: '#34d399', bg: 'rgba(52, 211, 153, 0.12)' },
  { tag: 'workflow:done', key: 'done', label: 'Done', shortLabel: 'Done', rank: 11, color: '#22c55e', bg: 'rgba(34, 197, 94, 0.12)' },
];

const STAGE_BY_TAG = new Map(STAGES.map((stage) => [stage.tag, stage]));

export function workflowTags(task: Pick<TaskItem, 'tags'>): string[] {
  return (task.tags ?? []).filter((tag) => tag.startsWith('workflow:'));
}

export function getWorkflowStage(task: Pick<TaskItem, 'tags'>): {
  stage: WorkflowStage | null;
  workflowTags: string[];
  hasConflict: boolean;
} {
  const tags = workflowTags(task);
  const stages = tags
    .map((tag) => STAGE_BY_TAG.get(tag))
    .filter((stage): stage is WorkflowStage => Boolean(stage))
    .sort((a, b) => b.rank - a.rank);
  return {
    stage: stages[0] ?? null,
    workflowTags: tags,
    hasConflict: tags.length > 1,
  };
}

export function tagsWithPrefix(task: Pick<TaskItem, 'tags'>, prefix: string): string[] {
  return (task.tags ?? []).filter((tag) => tag.startsWith(prefix));
}

export function hasTag(task: Pick<TaskItem, 'tags'>, tag: string): boolean {
  return (task.tags ?? []).includes(tag);
}

export function agentCompletionPolicy(task: Pick<TaskItem, 'tags'>): 'allowed' | 'blocked' | 'unspecified' {
  if (hasTag(task, 'agent-can-complete:false')) return 'blocked';
  if (hasTag(task, 'agent-can-complete:true')) return 'allowed';
  return 'unspecified';
}

export function workflowStageOptions(): WorkflowStage[] {
  return STAGES;
}
